import { OnboardingProgress } from '../../domain/entities/OnboardingProgress';
import { OnboardingStep } from '../../domain/entities/OnboardingStep';
import { OnboardingProgressDTO } from '../dto/OnboardingProgressDTO';
import { OnboardingStepDTO } from '../dto/OnboardingStepDTO';

/**
 * OnboardingMapper
 * Converts between domain entities and DTOs for data transfer
 */
export class OnboardingMapper {
  /**
   * Convert OnboardingProgress entity to DTO
   */
  static progressToDTO(progress: OnboardingProgress): OnboardingProgressDTO {
    const plainProgress = progress.toPlainObject();

    return {
      id: plainProgress.id,
      userId: plainProgress.userId,
      role: plainProgress.role,
      status: plainProgress.status,
      currentStepIndex: plainProgress.currentStepIndex,
      totalSteps: plainProgress.totalSteps,

      // Computed fields
      progressPercentage: progress.getProgressPercentage(),
      isFirstStep: progress.isFirstStep(),
      isLastStep: progress.isLastStep(),
      shouldShowOnboarding: progress.shouldShowOnboarding(),
      hasFinished: progress.hasFinished(),

      // Timestamps as ISO strings
      startedAt: plainProgress.startedAt ? plainProgress.startedAt.toISOString() : null,
      completedAt: plainProgress.completedAt ? plainProgress.completedAt.toISOString() : null,
      skippedAt: plainProgress.skippedAt ? plainProgress.skippedAt.toISOString() : null,
      createdAt: plainProgress.createdAt.toISOString(),
      updatedAt: plainProgress.updatedAt.toISOString(),
    };
  }

  /**
   * Convert OnboardingStep entity to DTO
   */
  static stepToDTO(step: OnboardingStep): OnboardingStepDTO {
    const plainStep = step.toPlainObject();

    return {
      id: plainStep.id,
      title: plainStep.title,
      description: plainStep.description,
      targetSelector: plainStep.targetSelector,
      position: plainStep.position,
      type: plainStep.type,
      route: plainStep.route,
      requiredAction: plainStep.requiredAction,
      imageUrl: plainStep.imageUrl,
      videoUrl: plainStep.videoUrl,
      order: plainStep.order,

      // Computed fields
      hasTarget: step.hasTarget(),
      requiresNavigation: step.requiresNavigation(),
      requiresAction: step.requiresAction(),
      hasMedia: step.hasMedia(),
    };
  }

  /**
   * Convert multiple OnboardingStep entities to DTOs
   */
  static stepsToDTOList(steps: OnboardingStep[]): OnboardingStepDTO[] {
    return steps.map(step => this.stepToDTO(step));
  }

  /**
   * Convert OnboardingProgressDTO to entity
   * Used when reconstructing entity from stored DTO
   */
  static dtoToProgress(dto: OnboardingProgressDTO): OnboardingProgress {
    return new OnboardingProgress({
      id: dto.id,
      userId: dto.userId,
      role: dto.role,
      status: dto.status,
      currentStepIndex: dto.currentStepIndex,
      totalSteps: dto.totalSteps,
      startedAt: dto.startedAt ? new Date(dto.startedAt) : null,
      completedAt: dto.completedAt ? new Date(dto.completedAt) : null,
      skippedAt: dto.skippedAt ? new Date(dto.skippedAt) : null,
      createdAt: new Date(dto.createdAt),
      updatedAt: new Date(dto.updatedAt),
    });
  }

  /**
   * Convert OnboardingStepDTO to entity
   * Used when reconstructing entity from stored DTO
   */
  static dtoToStep(dto: OnboardingStepDTO): OnboardingStep {
    return new OnboardingStep({
      id: dto.id,
      title: dto.title,
      description: dto.description,
      targetSelector: dto.targetSelector,
      position: dto.position,
      type: dto.type,
      route: dto.route,
      requiredAction: dto.requiredAction,
      imageUrl: dto.imageUrl,
      videoUrl: dto.videoUrl,
      order: dto.order,
    });
  }
}
